import { getQuestions } from '@/services/api/question';
import { useAuthStore } from '@/store';
import type { QuestionInfoType } from '@/types/api/idea-starter';
import type { QuestionsListingRequest } from '@/types/api/question';
import { useInfiniteQuery } from '@tanstack/react-query';

export const useQuestion = ({
  limit = 10,
  categoryId,
  discussionCategoryId,
  page = 1,
  status,
  type,
  userId,
  source,
  industryIds,
  occupationIds,
  search,
  trending,
  recentActivity,
}: Partial<QuestionsListingRequest> & {
  source?: 'add-story' | 'activity';
}) => {
  const token = useAuthStore((state) => state.token);
  const isLoggedIn = !!token;
  const isHydrated = useAuthStore((state) => state.isHydrated);

  // Add infinite query support
  const getInfiniteQuestionsApi = useInfiniteQuery({
    queryKey: [
      'infinite-questions-listing',
      limit.toString(),
      categoryId ? JSON.stringify(categoryId) : 'null',
      status || 'null',
      type || 'null',
      userId || 'null',
      source || 'null',
      discussionCategoryId ? JSON.stringify(discussionCategoryId) : 'null',
      industryIds ? JSON.stringify(industryIds) : 'null',
      occupationIds ? JSON.stringify(occupationIds) : 'null',
      search || 'null',
      trending || 'null',
      recentActivity || 'null',
    ],
    initialPageParam: 1,
    enabled: isHydrated,
    queryFn: ({ pageParam }) =>
      getQuestions(
        {
          page: pageParam,
          limit: limit,
          categoryId: categoryId || [],
          status: status,
          type: type,
          userId: userId,
          discussionCategoryId,
          industryIds,
          occupationIds,
          search,
          trending,
          recentActivity,
        },
        isLoggedIn,
        source
      ),
    getNextPageParam: (lastPage, _, lastPageParam) => {
      // Calculate if there are more pages
      const totalPages = Math.ceil(lastPage.totalCount / limit);
      if (lastPageParam < totalPages) {
        return lastPageParam + 1;
      }
      return undefined;
    },
    refetchOnWindowFocus: false,
  });

  // Helper function to get all questions from infinite query
  const getAllQuestions = (): QuestionInfoType[] => {
    if (!getInfiniteQuestionsApi.data) return [];

    // Flatten the pages and extract all questions
    return getInfiniteQuestionsApi.data.pages.reduce<QuestionInfoType[]>(
      (acc, page) => [...acc, ...page.data],
      []
    );
  };

  return {
    getInfiniteQuestionsApi,
    getAllQuestions,
  };
};
